import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useTranslation } from '../translations';
import { useAuth } from '../contexts/AuthContext';
import { redirectToCheckout, STRIPE_PRICE_IDS } from '../services/stripeService';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { HiOfficeBuilding } from 'react-icons/hi';
import { FaBalanceScale, FaHandshake } from 'react-icons/fa';
import appScreenshot from '../assets/app-screenshot.png';
import demoScreenshot from '../assets/demo-screenshot.png';
import annualZapIcon from '../assets/annual-zap-icon.png';
import pricingLogoClean from '../assets/pricing-logo-clean.png';
import { PLAN_CONFIGS, calculateYearlyPrice } from '../config/planConfig';
import { supabase } from '@/integrations/supabase/client';

const { FiBarChart2, FiCpu, FiShield, FiUsers, FiTrendingUp, FiTrendingDown, FiZap, FiTarget, FiActivity, FiLayers, FiCheckCircle, FiArrowRight, FiPlay, FiChevronDown, FiCheck, FiSend, FiMail, FiPhone, FiMapPin, FiDatabase, FiHeadphones, FiDollarSign, FiCamera, FiHome, FiFileText, FiClock, FiAlertTriangle } = FiIcons;

// Icon mapping for plans (maps plan id to icon)
const PLAN_ICONS = {
  starter: FiIcons.FiUser,
  pro_trader: FiTrendingUp,
  institutional_ai: HiOfficeBuilding,
  enterprise: FaHandshake,
};

// Pricing Section Component - USES CENTRALIZED PLAN CONFIG
const PricingSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);

  // Transform PLAN_CONFIGS to include runtime data (icons, Stripe IDs, yearly prices)
  // Hidden plans (e.g. Starter) are kept for existing subscribers but not shown here.
  const plans = PLAN_CONFIGS.filter(p => p.showOnPricing !== false).map(planConfig => ({
    id: planConfig.id,
    name: planConfig.name,
    subtitle: planConfig.subtitle,
    price: {
      monthly: planConfig.priceMonthly,
      yearly: calculateYearlyPrice(planConfig),
    },
    stripePriceId: planConfig.isEnterprise ? null : {
      monthly: STRIPE_PRICE_IDS[`${planConfig.id === 'pro_trader' ? 'pro' : planConfig.id === 'institutional_ai' ? 'institutional' : planConfig.id}_monthly`],
      yearly: STRIPE_PRICE_IDS[`${planConfig.id === 'pro_trader' ? 'pro' : planConfig.id === 'institutional_ai' ? 'institutional' : planConfig.id}_yearly`],
    },
    description: planConfig.description,
    features: planConfig.features,
    icon: PLAN_ICONS[planConfig.id] || FiIcons.FiUser,
    color: planConfig.id === 'free' ? 'from-cyan-500 to-blue-500' :
           planConfig.id === 'starter' ? 'from-blue-500 to-cyan-500' :
           planConfig.id === 'pro_trader' ? 'from-green-500 to-emerald-500' :
           planConfig.id === 'institutional_ai' ? 'from-purple-500 to-pink-600' :
           'from-yellow-500 to-orange-500',
    badge: planConfig.badge,
    isEnterprise: planConfig.isEnterprise,
  }));

  const handleSubscribe = async (plan) => {
    if (plan.isEnterprise) {
      // Scroll to contact section
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    // Redirect all plans to signup page
    navigate('/register');
  };

  return (
    <section id="pricing" className="relative py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Pricing Plans
            </span>
          </h2>
          <div className="mb-6">
            <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Trade With Institutional Discipline — Choose Your Level</span>
          </div>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Real-time risk decisions, institutional trade veto logic, and hedge-fund-grade analytics. Built for traders who want <span className="text-cyan-400 font-semibold">professional execution — not prediction games.</span>
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex justify-center items-center gap-4 mb-16"
        >
          <div className={`flex items-center gap-2 ${billingCycle === 'monthly' ? 'text-white' : 'text-gray-500'}`}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
              <span className="text-cyan-400 text-xs font-bold">30</span>
            </div>
            <span className="text-lg font-semibold">Monthly</span>
          </div>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className="relative inline-flex h-8 w-16 items-center rounded-full bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-9' : 'translate-x-1'
              }`}
            />
          </button>
          <div className={`flex items-center gap-2 ${billingCycle === 'yearly' ? 'text-white' : 'text-gray-500'}`}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
              <span className="text-cyan-400 text-xs font-bold">365</span>
            </div>
            <span className="text-lg font-semibold">Annual</span>
          </div>
          {billingCycle === 'yearly' && (
            <span className="ml-2 px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full">
              Save 20%
            </span>
          )}
        </motion.div>

        {/* Pricing Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * index }}
              className="relative group w-full"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-cyan-400 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
              
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/10 hover:border-purple-400/50 rounded-3xl p-6 h-full flex flex-col transition-all">
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <SafeIcon icon={plan.icon} className="w-6 h-6 text-cyan-400" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-1 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  {plan.name}
                </h3>
                {plan.subtitle && (
                  <p className="text-xs text-gray-500 mb-2">{plan.subtitle}</p>
                )}
                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                
                <div className="mb-6">
                  {plan.isEnterprise ? (
                    <>
                      <div className="text-3xl font-black bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
                        Custom Pricing
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        Contact Us
                      </div>
                    </>
                  ) : plan.price.monthly === 0 ? (
                    <>
                      <div className="text-4xl font-black bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
                        Free
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        No card required
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl font-black bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
                        ${plan.price[billingCycle]} <span className="text-2xl">USD</span>
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        per {billingCycle === 'monthly' ? 'month' : 'year'}
                      </div>
                    </>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <SafeIcon 
                        icon={feature.included ? FiCheck : FiCheck} 
                        className={`${feature.included ? 'text-green-400' : 'text-gray-600'} text-sm flex-shrink-0 mt-0.5`} 
                      />
                      <span className={`text-xs ${feature.included ? 'text-gray-300' : 'text-gray-600 line-through'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSubscribe(plan)}
                  disabled={loading}
                  className="w-full py-3 px-4 rounded-xl font-bold text-cyan-400 transition-all bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 shadow-lg hover:shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <SafeIcon icon={FiCpu} className="animate-spin" />
                    </span>
                  ) : plan.isEnterprise ? (
                    'Get Quote'
                  ) : plan.price.monthly === 0 ? (
                    'Start Free'
                  ) : (
                    'Get Started'
                  )}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 flex flex-wrap justify-center gap-8 text-gray-400"
        >
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiShield} className="text-cyan-400" />
            <span>Capital Preservation Engine Included</span>
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiClock} className="text-cyan-400" />
            <span>30-Day Institutional Trial</span>
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="text-cyan-400" />
            <span>No Commitments or Lock-Ins</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="relative py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Contact Our Institutional Support Team
            </span>
          </h2>
          <div className="mb-6">
            <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Dedicated Support for Traders, Institutions & Enterprise Partners</span>
          </div>
          <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
            Our team is available Monday to Friday, 10am–5pm CST. We typically <span className="text-cyan-400 font-semibold">respond within 24 business hours.</span>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 gap-12 bg-gray-800/50 backdrop-blur-sm border border-white/10 p-8 md:p-12 rounded-3xl"
        >
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold mb-4 text-white">Institutional Contact Directory</h3>
              <p className="text-gray-400 mb-6">We'd love to hear from you. Send us a message and our team will respond as soon as possible.</p>
              <div className="mb-6 space-y-2">
                <p className="font-semibold text-lg bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  SnapChart AI Inc.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiHeadphones} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">support@snap-chart.ai</span>
                  <p className="text-gray-500 text-sm">Client support, account assistance, platform help</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiDollarSign} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">sales@snap-chart.ai</span>
                  <p className="text-gray-500 text-sm">Institutional onboarding, licensing, enterprise plans</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiCamera} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">media@snap-chart.ai</span>
                  <p className="text-gray-500 text-sm">Press, analyst briefings, public relations</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiUsers} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">partnerships@snap-chart.ai</span>
                  <p className="text-gray-500 text-sm">Strategic partnerships, integrations, corporate collaborations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="w-full">
            {submitted ? (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-700/50 p-8 rounded-2xl h-full flex flex-col items-center justify-center text-center"
              >
                <SafeIcon icon={FiCheckCircle} className="text-6xl text-green-400 mb-4"/>
                <h3 className="text-2xl font-bold mb-2 text-white">Message Sent!</h3>
                <p className="text-gray-300">Thank you for reaching out. We'll get back to you soon.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input type="text" id="name" required className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input type="email" id="email" required className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                  <textarea id="message" rows="5" required className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white"></textarea>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit" 
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 font-bold py-4 px-6 rounded-xl hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 transition-all shadow-lg hover:shadow-purple-500/30"
                >
                  <SafeIcon icon={FiSend} />
                  <span>Send Message</span>
                </motion.button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Mini CTA Component (réutilisable entre les sections)
const MiniCTA = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-cyan-600/20 to-pink-600/20 rounded-2xl blur-2xl"></div>
          
          {/* CTA Card */}
          <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-300 bg-clip-text text-transparent">
                Ready To Trade With Institutional Discipline?
              </span>
            </h3>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              Activate your AI Risk Officer and protect your capital using NO-TRADE intelligence, counterfactual proof, and FICO-style discipline scoring.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <button
                onClick={async () => {
                  if (!user) {
                    navigate('/register');
                    return;
                  }
                  setLoading(true);
                  try {
                    const priceId = STRIPE_PRICE_IDS.starter_monthly;
                    await redirectToCheckout(priceId, user.id, user.email);
                  } catch (error) {
                    console.error('Subscription error:', error);
                    alert('Failed to start subscription. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="group px-6 py-3 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-xl font-bold shadow-lg hover:shadow-purple-500/30 transition-all duration-300 hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-cyan-400"
              >
                {loading ? (
                  <SafeIcon icon={FiCpu} className="animate-spin" />
                ) : (
                  <>
                    <span>Activate Risk Officer Mode</span>
                    <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('pricing');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="px-6 py-3 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-xl font-semibold hover:from-purple-600/20 hover:to-cyan-600/20 hover:border-purple-500/40 transition-all duration-300 text-gray-300 hover:text-cyan-400"
              >
                View Institutional Pricing
              </button>
            </div>
            
            {/* Institutional Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiShield} className="text-cyan-400" />
                <span className="font-medium">Capital Preservation Engine Included</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiCheckCircle} className="text-cyan-400" />
                <span className="font-medium">30-Day Institutional Trial</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiCheckCircle} className="text-cyan-400" />
                <span className="font-medium">No Commitments or Lock-Ins</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Redirect to dashboard if user is already logged in (e.g., after Google OAuth)
  useEffect(() => {
    if (user) {
      console.log('✅ User already logged in on landing page, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);

  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const ySpring = useSpring(y, springConfig);

  const features = [
    {
      icon: FiShield,
      badge: 'PROPRIETARY',
      badgeType: 'Risk Logic Engine',
      title: 'Institutional Risk Officer AI',
      subtitle: 'SnapChart AI acts as your personal risk committee:',
      bullets: [
        'TRADE / NO-TRADE / WAIT decisions',
        'Regime analysis (Risk-On, Risk-Off, Volatility Shock)',
        'Institutional veto logic to block bad setups',
        'Emotional-trading detection'
      ],
      footer: 'Modeled after hedge-fund risk protocols.',
      delay: 0.1
    },
    {
      icon: FiTrendingDown,
      badge: 'EXCLUSIVE',
      badgeType: 'Accountability Framework',
      title: 'Counterfactual Engine',
      subtitle: 'Every analysis is tracked over time to answer:',
      bullets: [
        'What if you didn\'t take the trade?',
        'Did you avoid a loss?',
        'Did your discipline protect your capital?'
      ],
      footer: 'Creates measurable improvement and proof of discipline.',
      delay: 0.2
    },
    {
      icon: FiBarChart2,
      badge: 'INSTITUTIONAL',
      badgeType: 'Performance Scoring',
      title: 'Trader Discipline Index (TDI)',
      subtitle: 'A professional-grade scoring system:',
      bullets: [
        '300–850 discipline score',
        'Tracks emotional trades',
        'Measures consistency',
        'Monitors risk behavior'
      ],
      footer: 'The first metric proven to improve trader behavior.',
      delay: 0.3
    },
    {
      icon: FiTarget,
      badge: 'ADVANCED',
      badgeType: 'Institutional Setup Builder',
      title: 'Institutional Trade Plan Generator',
      subtitle: 'If the AI approves the setup, you get:',
      bullets: [
        'Entry, SL, TP ladder',
        'R-multiple & invalidation',
        'Confidence score & scenario map',
        'Liquidity / imbalance breakdown'
      ],
      footer: 'Trade plans built like a hedge-fund analyst.',
      delay: 0.4
    },
    {
      icon: FiLayers,
      badge: 'MULTI-ASSET',
      badgeType: 'Cross-Market Validation',
      title: 'Multi-Chart Institutional Analysis',
      subtitle: 'Upload up to 10 charts and SnapChart AI will:',
      bullets: [
        'Compare structure & trend alignment',
        'Merge liquidity zones',
        'Generate a cross-market confirmation report'
      ],
      footer: 'Perfect for crypto, FX, indices, futures, multi-asset traders.',
      delay: 0.5
    },
    {
      icon: FiActivity,
      badge: 'REAL-TIME',
      badgeType: 'Market Risk Monitoring',
      title: 'News, Macro & Market Regime Intelligence',
      subtitle: 'Real-time integration with:',
      bullets: [
        'Macro risk warnings',
        'Scheduled news detection',
        'Volatility regime shifts',
        'Market impact scores'
      ],
      footer: 'Shows you when not to trade — the most important edge.',
      delay: 0.6
    }
  ];

  const stats = [
    { value: 'shield', label: 'AI Risk Officer', description: 'Real-time institutional trade vetoing based on regime, structure, and risk.', icon: FiShield },
    { value: 'trending-down', label: 'Capital Preservation', description: 'Track how much money you didn\'t lose with counterfactual proof.', icon: FiTrendingDown },
    { value: 'bar-chart', label: 'TDI Score (300-850)', description: 'FICO-like score that quantifies your discipline over time.', icon: FiBarChart2 },
    { value: 'brain', label: 'Impulsive Benchmark', description: 'See how badly the impulsive version of you would have lost.', icon: FiCpu }
  ];

  // Real testimonials live in the public.testimonials Supabase table.
  // No hardcoded fallback — better to show fewer real ones than fake ones.
  const [testimonials, setTestimonials] = useState([]);
  const [videoModalUrl, setVideoModalUrl] = useState(null);

  // Fetch approved testimonials from database with user avatars
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        // First get testimonials
        const { data: testimonialsData, error } = await supabase
          .from('testimonials')
          .select('id, user_id, user_name, text_content, video_url, type, is_featured')
          .eq('status', 'approved')
          .order('is_featured', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(12);

        if (error) {
          console.error('Error fetching testimonials:', error);
          return;
        }

        if (testimonialsData && testimonialsData.length > 0) {
          // Get user avatars for all testimonial users
          const userIds = testimonialsData.map(t => t.user_id).filter(Boolean);
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('user_id, avatar_url')
            .in('user_id', userIds);

          const avatarMap = new Map(profiles?.map(p => [p.user_id, p.avatar_url]) || []);

          // Map DB testimonials to display format - NO screenshots on home page
          const dbTestimonials = testimonialsData.map((t) => ({
            id: t.id,
            name: t.user_name || 'Verified Trader',
            role: 'SnapChart AI User',
            image: avatarMap.get(t.user_id) || null, // Use real avatar or null
            content: t.text_content || '',
            type: t.type,
            videoUrl: t.video_url,
            isFeatured: t.is_featured
          })).filter(t => t.content || t.videoUrl); // Only show if has content or video

          // Show ONLY real DB testimonials. No fallback to fakes.
          setTestimonials(dbTestimonials);
        }
      } catch (err) {
        console.error('Error fetching testimonials:', err);
      }
    };

    fetchTestimonials();
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white overflow-x-hidden">

      {/* Hero Section - What is Snap Chart AI */}
      <section id="what" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-96 h-96 bg-cyan-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-96 h-96 bg-pink-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
          
          {/* Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f12_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f12_1px,transparent_1px)] bg-[size:64px_64px]"></div>
        </div>

        <motion.div 
          style={{ opacity, scale }}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          {/* Premium Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
            </span>
            <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              The AI that tells you when NOT to trade.
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl lg:text-8xl font-black mb-4 leading-tight"
          >
            <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-300 bg-clip-text text-transparent">
              SnapChart AI
            </span>
          </motion.h1>
          
          {/* Taglines with gradient */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center mb-6"
          >
            <div>
              <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 via-purple-300 to-cyan-400 bg-clip-text text-transparent">
                Screenshot a chart.
              </span>
            </div>
            <div className="mt-1">
              <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">
                Get an A-to-F grade in seconds.
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-4">
              Most traders don't lose because they lack information.<br />
              They lose because they ignore what they already know.
            </p>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-6">
              SnapChart AI is your AI Risk Officer.<br />
              Before you risk a dollar — it tells you if the setup is worth it.
            </p>
            <p className="text-lg md:text-xl text-cyan-400 font-medium mb-6">
              Not signals. Not predictions. Just one clear answer: trade this, or walk away.
            </p>
            <div className="inline-flex items-center gap-2 text-sm text-gray-400 px-4 py-2 bg-gray-800/30 border border-white/5 rounded-full">
              <SafeIcon icon={FiCheck} className="text-cyan-400" />
              <span>Used by prop traders. Built for discipline.</span>
            </div>
          </motion.div>

          {/* CTA Button — primary action only (demo button removed pending stronger demo asset) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col items-center justify-center gap-2 mb-16"
          >
            <Link
              to="/register"
              className="group relative px-8 py-4 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-xl font-bold text-lg shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-105 flex items-center gap-3 text-cyan-400"
            >
              <span>Start your free 30-day trial</span>
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="text-xs text-gray-500 mt-1">No credit card required</p>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300 h-full">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                      <SafeIcon icon={stat.icon} className="text-3xl text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-lg font-bold text-white mb-2 text-center">{stat.label}</div>
                  <div className="text-xs text-gray-400 text-center leading-relaxed">{stat.description}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-white/50 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Trusted By Logos Banner */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-widest text-white mb-8 font-bold">TRUSTED BY TRADERS USING</p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              <a href="https://www.binance.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">Binance</a>
              <a href="https://www.kucoin.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">KuCoin</a>
              <a href="https://www.kraken.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">Kraken</a>
              <a href="https://www.htx.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">HTX</a>
              <a href="https://www.coinbase.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">Coinbase</a>
              <a href="https://crypto.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">Crypto.com</a>
            </div>
            
            {/* Brokers Section */}
            <div className="mt-16 flex flex-wrap items-center justify-center gap-8 md:gap-16">
              <a href="https://www.vantagemarkets.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">VantageMarkets</a>
              <a href="https://www.icmarkets.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">IC Markets</a>
              <a href="https://www.blackbull.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">BlackBull</a>
              <a href="https://www.interactivebrokers.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">Interactive Brokers</a>
              <a href="https://www.etrade.com" target="_blank" rel="noopener noreferrer" className="text-2xl md:text-3xl font-black text-gray-400/70 hover:text-white transition-colors cursor-pointer">E*TRADE</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA after Trusted By */}
      <MiniCTA />

      {/* Why Section */}
      <section id="why" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Institutional-Grade Risk Intelligence
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Why SnapChart AI?
              </span>
            </h3>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Because Discipline Outperforms Predictions.</span>
            </div>
            <div className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              <p>Retail traders chase entries. Professionals manage risk, behavior, and capital preservation. SnapChart AI is the first AI built on institutional discipline systems — not signals, not hype, not predictions. <span className="text-cyan-400 font-medium">Your edge is not knowing more. Your edge is avoiding the trades that destroy your account.</span></p>
            </div>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'AI Risk Officer Mode',
                subtitle: 'Your personal hedge-fund risk committee.',
                bullets: [
                  'NO-TRADE decisions that protect capital',
                  'Institutional veto logic',
                  'Regime-based risk filters',
                  'Emotional-trading detection'
                ],
                footer: 'This is the only AI that prevents you from making bad trades.',
                icon: FiShield,
                delay: 0.1
              },
              {
                title: 'Counterfactual Performance Engine',
                subtitle: 'Proof-based improvement, not guesswork.',
                bullets: [
                  'What would have happened if you traded',
                  'How much capital you saved',
                  'Whether discipline protected you'
                ],
                footer: 'You get measurable, verifiable proof — not opinions.',
                icon: FiTrendingDown,
                delay: 0.2
              },
              {
                title: 'Trader Discipline Index (TDI)',
                subtitle: 'The first performance score that actually makes traders better.',
                bullets: [
                  'Consistency tracking',
                  'Risk avoidance measurement',
                  'Emotional stability scoring',
                  'Behavioral reliability monitoring'
                ],
                footer: 'Professionals get better by tracking behavior — not charts.',
                icon: FiBarChart2,
                delay: 0.3
              },
              {
                title: 'Impulsive Trader Benchmark',
                subtitle: 'See exactly what undisciplined trading would have cost you.',
                bullets: [
                  'Quantifies losses avoided',
                  'Measures behavioral alpha gained',
                  'Shows the value of saying NO-TRADE'
                ],
                footer: 'This is how traders finally understand the cost of their impulses.',
                icon: FiCpu,
                delay: 0.4
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: feature.delay }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-500">
                  {/* Icon - styled like Powerful Features */}
                  <div className="flex justify-start mb-6">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={feature.icon} className="text-3xl text-cyan-400" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h4 className="text-2xl font-bold mb-2 text-white group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h4>
                  
                  {/* Subtitle */}
                  <p className="text-gray-300 mb-4 font-medium">
                    {feature.subtitle}
                  </p>
                  
                  {/* Bullets */}
                  <ul className="space-y-2 mb-5">
                    {feature.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                        <SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Footer */}
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-cyan-400 text-sm font-medium">
                      {feature.footer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA after Why */}
      <MiniCTA />

      {/* How Section - Four Steps */}
      <section id="how" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Institutional Workflow
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                How It Works
              </span>
            </h3>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Institutional Trading Discipline in Four Steps</span>
            </div>
            <div className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed mb-8">
              <p>SnapChart AI replicates the same risk, discipline, and <span className="text-cyan-400 font-semibold">capital-preservation systems</span> used by top hedge funds. Every analysis includes:</p>
            </div>
            {/* Intro Bullets */}
            <div className="flex flex-col md:flex-row flex-wrap md:flex-nowrap justify-center items-center gap-3 md:gap-4 max-w-5xl mx-auto">
              {['Institutional risk officer logic', 'Market regime classification', 'Emotional-trading filters', 'Post-decision accountability'].map((item, i) => (
                <span key={i} className="px-4 py-2 bg-gray-800/50 border border-white/10 rounded-full text-sm text-gray-300 flex items-center gap-2 whitespace-nowrap">
                  <SafeIcon icon={FiCheck} className="text-cyan-400" />
                  {item}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Steps Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                step: '1',
                title: 'Upload a Chart',
                subtitle: 'Institutional AI reads your chart like a hedge-fund analyst.',
                bullets: [
                  'Market structure',
                  'Liquidity zones',
                  'Volatility conditions',
                  'Key levels & invalidation points'
                ],
                footer: 'Your chart becomes a fully mapped institutional dataset in seconds.',
                delay: 0.1
              },
              {
                step: '2',
                title: 'AI Risk Officer Analysis',
                subtitle: 'Your personal risk committee — automated.',
                bullets: [
                  'TRADE / NO-TRADE / WAIT / CONDITIONAL decisions',
                  'Market regime detection',
                  'Risk-on/risk-off classification',
                  'Veto filters to block losing setups'
                ],
                footer: 'This is the only AI that stops you from taking bad trades.',
                delay: 0.2
              },
              {
                step: '3',
                title: 'Institutional Trade Plan',
                subtitle: 'A full hedge-fund trading plan — if and only if the setup qualifies.',
                bullets: [
                  'Entry, stop loss, take profit ladder',
                  'R-multiple analysis',
                  'Liquidity & imbalance breakdown',
                  'Confidence score & scenario map'
                ],
                footer: 'Every decision is explainable, defensible, and accountable.',
                delay: 0.3
              },
              {
                step: '4',
                title: 'Proof & Discipline Tracking',
                subtitle: 'Your trading becomes measurable and improvable.',
                bullets: [
                  'Counterfactual tracking (1h / 4h / 24h)',
                  'Impulsive Trader Benchmark (losses avoided)',
                  'Trader Discipline Index (TDI score)',
                  'Full audit trail for every decision'
                ],
                footer: 'You get proof that your discipline is improving over time.',
                delay: 0.4
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: item.delay }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-500">
                  {/* Step Badge - styled like Powerful Features icons */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-2xl font-black bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{item.step}</span>
                    </div>
                    <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Step {item.step}</span>
                  </div>
                  
                  {/* Title */}
                  <h4 className="text-2xl font-bold mb-3 text-white group-hover:text-cyan-400 transition-colors">
                    {item.title}
                  </h4>
                  
                  {/* Subtitle */}
                  <p className="text-gray-300 mb-4 font-medium">
                    {item.subtitle}
                  </p>
                  
                  {/* Bullets */}
                  <ul className="space-y-2 mb-5">
                    {item.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                        <SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Footer */}
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-cyan-400 text-sm font-medium">
                      {item.footer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA after How */}
      <MiniCTA />

      {/* Features Section - Premium */}
      <section id="features" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Institutional Analytics Engine
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Powerful Features
              </span>
            </h3>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Institutional Features Designed to Give Retail Traders Professional Discipline</span>
            </div>
            <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              SnapChart AI brings hedge-fund-level risk systems, accountability tools, and structured analysis to everyday traders — <span className="text-cyan-400 font-semibold">giving you an edge that used to require millions in infrastructure.</span>
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: feature.delay }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-500">
                  {/* Feature Badge */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-2 py-1 text-[10px] font-bold tracking-wider bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 rounded text-cyan-300 uppercase">
                      {feature.badge}
                    </span>
                    <span className="text-xs text-gray-400 font-medium">{feature.badgeType}</span>
                  </div>
                  
                  {/* Icon - styled like What section */}
                  <div className="flex justify-start mb-6">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={feature.icon} className="text-3xl text-cyan-400" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h4 className="text-xl font-bold mb-4 text-white group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h4>
                  
                  {/* Core Functions Header */}
                  <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-2">Core Functions:</p>
                  <p className="text-gray-400 text-sm mb-3">
                    {feature.subtitle}
                  </p>
                  
                  {/* Bullets */}
                  <ul className="space-y-2 mb-5">
                    {feature.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Edge Footer */}
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-1">Edge:</p>
                    <p className="text-cyan-400 text-sm font-medium">
                      {feature.footer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA after Features */}
      <MiniCTA />

      {/* Platform Preview Section - Billion Dollar Version */}
      <section id="demo" className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                🎥 Live Platform Demo
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Watch Snap Chart AI in Action
              </span>
            </h2>
            <div className="mb-6">
              <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                See Your AI Risk Officer Analyze Markets in Real Time
              </span>
            </div>
            <div className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed space-y-4">
              <p>
                Snap Chart AI doesn't just analyze charts — it behaves like a <span className="text-cyan-400 font-semibold">hedge fund risk officer</span>, scanning structure, liquidity, volatility, and behavioral risk before approving or vetoing a trade.
              </p>
            </div>
          </motion.div>

          {/* What Snap Chart AI Does */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12"
          >
            <p className="text-center text-gray-300 mb-6 text-lg">Watch how Snap Chart AI:</p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                "Detects hidden structure, liquidity zones, and volatility shifts",
                "Classifies the market regime in seconds",
                "Generates an institutional-grade trade plan (or issues a NO-TRADE veto)",
                "Produces counterfactual capital-preservation tracking",
                "Calculates your TDI (Trader Discipline Index) score in real time"
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-white/10 rounded-xl"
                >
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Platform Screenshot */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 60 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative mb-12"
          >
            {/* Glow Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 via-cyan-600/30 to-pink-600/30 rounded-3xl blur-3xl"></div>
            
            {/* Real App Screenshot */}
            <div className="relative group cursor-pointer" onClick={() => setShowVideoModal(true)}>
              <img 
                src={demoScreenshot} 
                alt="Snap Chart AI Trading Platform - Real-time AI analysis interface" 
                className="w-full rounded-2xl shadow-2xl border border-white/10"
              />
              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                  <FiPlay className="text-4xl md:text-5xl text-white ml-1" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Two Panels: Left (Role) + Right (Report Preview) */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid md:grid-cols-2 gap-8 mb-12"
          >
            {/* Left Panel - Role Loaded */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
              <div className="relative bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-cyan-500/30 transition-all duration-300 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                    <SafeIcon icon={FiShield} className="text-xl text-cyan-400" />
                  </div>
                  <h4 className="text-lg font-bold text-white">Role Loaded: AI Risk Officer Mode</h4>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Using institutional risk filters, volatility classification, structure detection, and discipline metrics.
                </p>
              </div>
            </div>

            {/* Right Panel - Report Preview */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
              <div className="relative bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-cyan-500/30 transition-all duration-300 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                    <SafeIcon icon={FiFileText} className="text-xl text-cyan-400" />
                  </div>
                  <h4 className="text-lg font-bold text-white">Your Institutional Report Will Appear Here</h4>
                </div>
                <p className="text-gray-400 text-sm mb-4">Generated using:</p>
                <div className="flex flex-wrap gap-2">
                  {["Regime classification", "Capital-risk filters", "Trade/no-trade framework", "Counterfactual engine", "Emotional-trading detection"].map((item, i) => (
                    <span key={i} className="px-3 py-1 text-xs bg-gray-700/50 border border-white/10 rounded-full text-gray-300">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Multi-Chart Feature */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
            <div className="relative bg-gradient-to-r from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                  <SafeIcon icon={FiLayers} className="text-3xl text-cyan-400" />
                </div>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">✓ Multi-Chart Institutional Scanning</h4>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Upload up to 10 charts simultaneously and Snap Chart AI will merge structure, validate multi-timeframe alignment, and produce a unified market decision.
              </p>
            </div>
          </motion.div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-12"
          >
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              This isn't "AI chart analysis."<br />
              <span className="text-white font-semibold">This is institutional risk intelligence, automated for everyday traders.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA after Demo */}
      <MiniCTA />

      {/* Pricing Section */}
      <PricingSection />

      {/* CTA after Pricing */}
      <MiniCTA />

      {/* Testimonials Section */}
      <section id="testimonials" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Trusted by Institutions And Professional Traders
              </span>
            </h2>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Real Results from Real Institutional Traders</span>
            </div>
            <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              <span className="text-cyan-400 font-semibold">Trusted by disciplined traders worldwide.</span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 6).map((testimonial, index) => (
              <motion.div
                key={testimonial.id || testimonial.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-6">
                    {testimonial.image ? (
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full border-2 border-cyan-500 object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full border-2 border-cyan-500 bg-gradient-to-br from-purple-600/30 to-cyan-600/30 flex items-center justify-center">
                        <span className="text-2xl font-bold text-white/80">
                          {testimonial.name?.charAt(0)?.toUpperCase() || 'T'}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="font-bold text-white flex items-center gap-2">
                        {testimonial.name}
                        {testimonial.type === 'video' && (
                          <span className="text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded-full">Video</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">{testimonial.role}</div>
                    </div>
                  </div>
                  
                  {/* Video testimonial */}
                  {testimonial.type === 'video' && testimonial.videoUrl ? (
                    <div className="space-y-3">
                      {testimonial.content && (
                        <p className="text-gray-300 leading-relaxed italic text-sm">
                          "{testimonial.content}"
                        </p>
                      )}
                      <button
                        onClick={() => setVideoModalUrl(testimonial.videoUrl)}
                        className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        <SafeIcon icon={FiPlay} className="w-5 h-5" />
                        Watch Video Testimonial
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-300 leading-relaxed italic">
                      "{testimonial.content}"
                    </p>
                  )}
                  
                  <div className="mt-6 flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon key={i} icon={FiCheckCircle} className="text-yellow-400" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {videoModalUrl && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setVideoModalUrl(null)}
        >
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <video
              src={videoModalUrl}
              controls
              autoPlay
              className="w-full rounded-xl"
            />
            <button
              onClick={() => setVideoModalUrl(null)}
              className="mt-4 px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white mx-auto block transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* CTA after Testimonials */}
      <MiniCTA />

      {/* FAQ Section */}
      <section id="faq" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Frequently Asked Questions
              </span>
            </h2>
            <p className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Everything You Need to Know
            </p>
            <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto mt-4 leading-relaxed">
              Clear answers to common questions about institutional-grade <span className="text-cyan-400 font-semibold">AI trading discipline.</span>
            </p>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                question: "How accurate is Snap Chart AI's analysis?",
                answer: "Snap Chart AI uses advanced institutional-grade models designed for structure, liquidity, and risk analysis—not predictions. Accuracy depends on market conditions, but our Risk Officer Mode, TDI Score, and counterfactual tracking ensure you receive disciplined, repeatable guidance instead of emotional or impulsive signals."
              },
              {
                question: "Can I use Snap Chart AI for different trading styles?",
                answer: "Yes. Snap Chart AI supports swing trading, intraday trading, scalping, crypto, FX, futures, and multi-asset strategies. The AI adapts the trade plan, risk filters, and confidence scoring to your preferred approach while maintaining institutional discipline."
              },
              {
                question: "What markets does Snap Chart AI support?",
                answer: "Snap Chart AI works with any chart you upload, including: Crypto (BTC, ETH, XRP, ADA, DOGE, AVAX, LINK, MATIC, DOT & SOL), Indices (NAS100, GER30, S&P500 & US30) and XAU (Gold). If it has price/structure/volume, Snap Chart AI can analyze it."
              },
              {
                question: "Do you offer a refund policy?",
                answer: "We offer a 30-day free institutional trial — completely risk-free. You can explore all features before committing, so there's no need for refunds. Try Snap Chart AI with zero financial risk and decide if it elevates your trading discipline."
              },
              {
                question: "How do I cancel my subscription?",
                answer: "You can cancel anytime with a single click in your account dashboard. Your access remains active for the duration of the billing period—no hidden steps, no support ticket required."
              },
              {
                question: "Can I switch between monthly and annual plans?",
                answer: "Absolutely. You can upgrade, downgrade, or switch plans at any time. Annual plans deliver the strongest value with significant savings and exclusive features."
              },
              {
                question: "Is my trading data secure?",
                answer: "Yes. Your data is encrypted, private, and never shared or sold. Snap Chart AI does not store charts long-term, does not access your brokerage account, and does not track personal trading history outside the app. Institutional-grade security and compliance are built in by design."
              },
              {
                question: "Can Snap Chart AI execute my trades for me?",
                answer: "No. Snap Chart AI is not an execution system and does not place trades. Instead, it acts as your AI Risk Officer—providing disciplined NO-TRADE, TRADE, and WAIT decisions with full accountability, audit tracking, and capital-preservation logic. You always retain full control over your trades."
              }
            ].map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-gray-800/30 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all duration-300"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="text-lg font-semibold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent pr-4">{faq.question}</span>
                  <SafeIcon 
                    icon={FiChevronDown} 
                    className="text-cyan-400 transition-transform duration-300 group-open:rotate-180 flex-shrink-0" 
                  />
                </summary>
                <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                  {faq.answer}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA after FAQ */}
      <MiniCTA />

      {/* Contact Section */}
      <ContactSection />

      {/* YouTube Video Modal - Rendered at root level to avoid z-index issues */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative w-full max-w-5xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-cyan-400 transition-colors z-10"
            >
              <FiIcons.FiX className="text-3xl" />
            </button>
            <iframe
              src="https://www.loom.com/embed/6e2c41a9969548fe92bc7cfc768bef9e?autoplay=1&hide_owner=true&hide_share=true&hide_title=true"
              title="Snap Chart AI Demo Video"
              className="w-full h-full rounded-2xl border border-white/20 shadow-2xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </motion.div>
        </div>
      )}

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;